import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileSignature, CheckCircle2, XCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignAgreement() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    axios.get(`${API}/agreements/sign/${token}`)
      .then(({ data }) => setMeta(data))
      .catch((e) => setError(e.response?.data?.detail || 'This signing link is invalid.'));
  }, [token]);

  const sign = async () => {
    if (!agree) { toast.error('Please confirm you have read and agree to the terms.'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/agreements/sign/${token}`,
        { action: 'sign', signature_full_name: name });
      setMeta((m) => ({ ...m, status: 'signed', signed_at: data.signed_at, signature_full_name: name }));
      toast.success('Agreement signed — a copy is on its way to your inbox.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signing failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/agreements/sign/${token}`,
        { action: 'decline', reason: declineReason });
      setMeta((m) => ({ ...m, status: 'declined' }));
      setShowDecline(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record your response.');
    } finally {
      setBusy(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <header className="bg-[#1A3626] py-4 text-center">
        <span className="font-serif text-xl tracking-[0.25em] text-[#F4E5C2]">MYLEAF LEGACY</span>
        <div className="text-[10px] tracking-[0.3em] text-[#C5A059] mt-0.5">· FOREVER HERE ·</div>
      </header>
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-8">{children}</main>
    </div>
  );

  if (error) {
    return (
      <Shell>
        <div className="text-center py-16" data-testid="sign-error">
          <XCircle size={40} className="mx-auto text-red-400 mb-4" />
          <h1 className="font-serif text-2xl text-[#1A3626]">{error}</h1>
          <p className="text-sm text-[#8C8273] mt-2">If you believe this is a mistake, please contact MyLeaf Legacy.</p>
        </div>
      </Shell>
    );
  }
  if (!meta) {
    return <Shell><div className="text-center py-16 text-[#8C8273]" data-testid="sign-loading">Loading your agreement…</div></Shell>;
  }

  if (meta.status === 'signed') {
    return (
      <Shell>
        <div className="text-center py-14" data-testid="sign-success">
          <CheckCircle2 size={44} className="mx-auto text-[#3F6B45] mb-4" />
          <h1 className="font-serif text-3xl text-[#1A3626]">Agreement signed</h1>
          <p className="text-sm text-[#5C5345] mt-3 max-w-md mx-auto">
            Thank you{meta.signature_full_name ? `, ${meta.signature_full_name}` : ''}. Your
            Independent Partner Agreement ({meta.version_label}) was signed
            {meta.signed_at ? ` on ${new Date(meta.signed_at).toLocaleString()}` : ''}.
            A signed copy has been emailed to you for your records.
          </p>
          <a href={`${API}/agreements/sign/${token}/pdf`} target="_blank" rel="noreferrer"
             data-testid="sign-view-signed-pdf"
             className="inline-block mt-6 px-6 py-2.5 rounded-full bg-[#1A3626] text-[#F4E5C2] text-xs uppercase tracking-[0.18em] font-semibold">
            View your signed PDF →
          </a>
        </div>
      </Shell>
    );
  }

  if (meta.status === 'declined') {
    return (
      <Shell>
        <div className="text-center py-16" data-testid="sign-declined">
          <XCircle size={40} className="mx-auto text-[#8C8273] mb-4" />
          <h1 className="font-serif text-2xl text-[#1A3626]">Agreement declined</h1>
          <p className="text-sm text-[#8C8273] mt-2">Your response has been recorded. MyLeaf Legacy will follow up with you.</p>
        </div>
      </Shell>
    );
  }

  if (['expired', 'voided', 'superseded'].includes(meta.status)) {
    return (
      <Shell>
        <div className="text-center py-16" data-testid="sign-unavailable">
          <Clock size={40} className="mx-auto text-[#C5A059] mb-4" />
          <h1 className="font-serif text-2xl text-[#1A3626]">
            This agreement is no longer available
          </h1>
          <p className="text-sm text-[#8C8273] mt-2 max-w-md mx-auto">
            {meta.status === 'expired'
              ? 'The signing window for this agreement has expired.'
              : 'This agreement has been replaced or withdrawn.'} Please contact
            MyLeaf Legacy for an updated copy.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="sign-agreement-page">
        <div className="flex items-center gap-2 text-[#C5A059] text-[10px] uppercase tracking-[0.25em] font-semibold">
          <FileSignature size={13} /> Signature requested
        </div>
        <h1 className="font-serif text-3xl text-[#1A3626] mt-2">
          Independent Partner Agreement
        </h1>
        <p className="text-sm text-[#5C5345] mt-1">
          {meta.version_label}
          {meta.kind === 'amendment' && (
            <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#FBF1D9] text-[#8B6A3E] font-semibold">
              Amendment — replaces prior versions upon signature
            </span>
          )}
        </p>
        {meta.expires_at && (
          <p className="text-[11px] text-[#8C8273] mt-1">
            Please sign by {new Date(meta.expires_at).toLocaleDateString()}.
          </p>
        )}

        <div className="mt-5 rounded-xl border border-[#E8E2D0] overflow-hidden bg-white shadow-sm">
          <div className="px-4 py-2.5 bg-[#FBF1D9]/60 border-b border-[#E8E2D0] flex items-center justify-between gap-3">
            <span className="text-[11px] text-[#5C5345]">Review the full agreement below before signing.</span>
            <a href={`${API}/agreements/sign/${token}/pdf`} target="_blank" rel="noreferrer"
               data-testid="sign-open-pdf-link"
               className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#8B6A3E] whitespace-nowrap hover:underline">
              Open PDF →
            </a>
          </div>
          <iframe
            title="Agreement PDF"
            src={`${API}/agreements/sign/${token}/pdf`}
            className="w-full"
            style={{ height: '65vh' }}
            data-testid="sign-pdf-frame"
          />
        </div>

        <div className="mt-6 rounded-xl border border-[#E8E2D0] bg-white p-5 shadow-sm">
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[#8C8273] font-semibold">
            Type your full legal name to sign
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jeffery Jay Bleim"
            data-testid="sign-name-input"
            className="mt-2 w-full border border-[#E8E2D0] rounded-lg px-4 py-3 font-serif italic text-xl text-[#1A3626] bg-[#FDFBF7]"
          />
          <label className="mt-4 flex items-start gap-2.5 text-[13px] text-[#5C5345] cursor-pointer">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                   data-testid="sign-agree-checkbox" className="mt-0.5" />
            <span>
              I have read and agree to the terms of this Independent Partner
              Agreement. I understand that typing my full legal name above
              constitutes my electronic signature, and that the date, time,
              and IP address of my signature will be recorded.
            </span>
          </label>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={sign}
              disabled={busy || !agree || name.trim().length < 3}
              data-testid="sign-submit-btn"
              className="px-7 py-3 rounded-full bg-[#1A3626] text-[#F4E5C2] text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-40"
            >
              {busy ? 'Signing…' : 'Sign agreement'}
            </button>
            <button
              onClick={() => setShowDecline(true)}
              disabled={busy}
              data-testid="sign-decline-btn"
              className="px-6 py-3 rounded-full border border-[#E8E2D0] text-[#8C8273] text-xs uppercase tracking-[0.2em] font-semibold"
            >
              Decline
            </button>
          </div>
        </div>

        {showDecline && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center p-4"
               data-testid="sign-decline-modal">
            <div className="bg-white rounded-2xl border border-[#E8E2D0] w-full max-w-md mt-24 p-6">
              <h3 className="font-serif text-xl text-[#1A3626]">Decline this agreement?</h3>
              <textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                        placeholder="Optional — tell us why so we can follow up"
                        data-testid="sign-decline-reason"
                        className="mt-3 w-full border border-[#E8E2D0] rounded-lg p-3 text-sm" rows={3} />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowDecline(false)} data-testid="sign-decline-cancel"
                        className="px-4 py-2 rounded-full border border-[#E8E2D0] text-sm text-[#5C5345]">Back</button>
                <button onClick={decline} disabled={busy} data-testid="sign-decline-confirm"
                        className="px-5 py-2 rounded-full bg-red-600 text-white text-sm font-medium disabled:opacity-50">
                  Decline agreement
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}
